import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import environment from '@environments/environment';
import {
  viewMoreDates,
  SubmissionResponse,
  CancelSubmission,
  OrganizationValidationResponse,
  UpdateSubmissionStagePayload,
} from '../types';
import { ApiListResponse } from '@documents/models';
import { organizationValidationPayload } from '../utils';

const SUBMISSIONS_URL = `${environment.irisApi}/v2/workspace`;

@Injectable({
  providedIn: 'root',
})
export class SubmissionsService {
  private readonly _http = inject(HttpClient);

  /*
   * A method to get more review dates for a given workspace.
   * @param workspaceId - The ID of the workspace to retrieve review dates for.
   * @returns An Observable of viewMoreDates containing the review dates data.
   */
  viewMoreDatesReview(workspaceId: string) {
    return this._http.get<viewMoreDates>(
      `${SUBMISSIONS_URL}/${workspaceId}/review_dates/`,
    );
  }

  /*
   * A method to get a submission by its ID.
   * @param id - The ID of the submission to retrieve.
   * @returns An Observable of SubmissionResponse containing the submission data.
   */
  getSubmission(id: string) {
    return this._http.get<SubmissionResponse>(`${SUBMISSIONS_URL}/${id}/`);
  }

  /*
   * A method to update the submission stage for a given workspace.
   * @param workspaceId - The ID of the workspace to update the submission stage for.
   * @returns An Observable of any type indicating the result of the update operation.
   */
  updateSubmissionStage({
    workspaceId,
    entityType,
    organizationValidationData,
    unassignedOrganizationTypes,
  }: UpdateSubmissionStagePayload) {
    const body =
      organizationValidationPayload(
        entityType,
        organizationValidationData,
        unassignedOrganizationTypes,
      ) || {};
    return this._http.put<SubmissionResponse>(
      `${SUBMISSIONS_URL}/${workspaceId}/stage/`,
      body,
    );
  }

  /**
   * Method to check if an organization is assigned to the submission.
   * This is required before moving to review stage, and needs to be checked before review stage transition as the assigned organization can be changed while the submission is in progress.
   * This also checks what the entity type of the submission is, which is required for the review stage transition.
   **/
  organizationValidation(workspaceId: string) {
    return this._http.get<OrganizationValidationResponse>(
      `${SUBMISSIONS_URL}/${workspaceId}/organization-validation/`,
    );
  }

  /*
   * A method to complete an IBC submission.
   * @param workspaceId - The ID of the workspace to complete the IBC submission for.
   * @returns An Observable of SubmissionResponse containing the updated submission data.
   */
  completeIbcSubmission(workspaceId: string) {
    return this._http.put<SubmissionResponse>(
      `${SUBMISSIONS_URL}/${workspaceId}/complete-ibc-submission/`,
      {},
    );
  }

  /*
   * A method to update the name of a submission.
   * @param workspaceId - The ID of the workspace to update the submission name for.
   * @param name - The new name for the submission.
   * @return An Observable of SubmissionResponse containing the updated submission data.
   */
  updateSubmissionName(workspaceId: string, name: string) {
    return this._http.put<SubmissionResponse>(
      `${SUBMISSIONS_URL}/${workspaceId}/`,
      {
        data: {},
        name,
      },
    );
  }

  cancelSubmission(workspaceId: string, reason_for_deletion: string) {
    return this._http.delete<CancelSubmission>(
      `${SUBMISSIONS_URL}/${workspaceId}/cancel-submission/`,
      {
        body: {
          reason_for_delete: reason_for_deletion,
        },
      },
    );
  }

  /**
   * A method to remove a submission by its workspace ID.
   * @param workspaceId - The ID of the workspace to remove.
   * @param reason_for_delete - The reason for deleting the submission.
   * @param isInternal - A boolean indicating if the user is internal or external (default is false).
   * @returns An Observable of any type indicating the result of the removal operation.
   */
  removeSubmission(
    workspaceId: string,
    reason_for_delete: string,
    isInternal = false,
  ) {
    // Determine the appropriate API URL based on user type
    const apiUrl = () => {
      if (isInternal) {
        /**
         * Remove a submission by its workspace id (internal user).
         */
        return `${environment.irisApi}/v2/workspace/admin/${workspaceId}/delete/`;
      }
      /**
       * Remove a submission by its workspace ID (external user).
       **/
      return `${environment.irisApi}/v2/workspace/${workspaceId}/delete/`;
    };
    return this._http.put(apiUrl(), {
      reason_for_delete,
    });
  }

  loadOutcomeDocuments(id: string) {
    return this._http.get<ApiListResponse<unknown>>(
      `${SUBMISSIONS_URL}/${id}/outcome-documents/`,
    );
  }
}
